import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IoImageOutline, IoCheckmarkCircle, IoAlertCircleOutline, IoArchiveOutline, IoArrowUndoOutline, IoDownloadOutline } from 'react-icons/io5';
import { Button } from 'react-bootstrap';
import { setPackagingUnitArchived, downloadDocumentationZip } from '../../../../store/actions/packagingUnit';
import { PACKAGING_TYPE_LABELS } from '../../../../utils/constants/packagingTypes';
import { PACKAGING_CHARACTER_LABELS } from '../../../../utils/constants/packagingCharacters';
import { getRoleBadges, getRequiredDocumentCategoryKeys } from '../../../../utils/constants/packagingRoles';
import Overview from './overview';
import DocumentsTab from './documentsTab';
import ConformityAssessmentTab from './conformityAssessmentTab';
import TechnicalDocumentationTab from './technicalDocumentationTab';
import EuDeclarationTab from './euDeclarationTab';
import MarkingTab from './markingTab';
import DistributorVerificationTab from './distributorVerificationTab';
import ProducerVerificationTab from './producerVerificationTab';

const DOCUMENT_TAB_DESCRIPTIONS = {
    SUPPLIER_DOCS: 'Certifikáty, technické listy, testovacie protokoly a ďalšie podklady od dodávateľov obalu alebo materiálu.',
    CONFORMITY_ASSESSMENT: 'Dokumentácia k posúdeniu zhody obalu s požiadavkami nariadenia PPWR.',
    EU_DECLARATION: 'EÚ vyhlásenie o zhode vydané zhotoviteľom obalu.',
    TECHNICAL_DOCS: 'Technická dokumentácia preukazujúca splnenie požiadaviek PPWR (napr. Príloha VII) — výpočty, testy, protokoly a súvisiace podklady.',
    DISTRIBUTOR_VERIFICATION: 'Overenie povinností podľa čl. 19 PPWR pred sprístupnením obalu na trhu (napr. registrácia výrobcu).',
    PRODUCER_VERIFICATION: 'Potvrdenie registrácie výrobcu podľa čl. 44 PPWR (rozšírená zodpovednosť výrobcu).',
};

const STEP_META = {
    SUPPLIER_DOCS: { label: 'Doklady', sublabel: 'od dodávateľov' },
    CONFORMITY_ASSESSMENT: { label: 'Posúdenie', sublabel: 'zhody' },
    EU_DECLARATION: { label: 'EÚ vyhlásenie', sublabel: 'o zhode' },
    TECHNICAL_DOCS: { label: 'Technická', sublabel: 'dokumentácia' },
    DISTRIBUTOR_VERIFICATION: { label: 'Overenie', sublabel: 'pred sprístupnením' },
    PRODUCER_VERIFICATION: { label: 'Registrácia', sublabel: 'výrobcu' },
};

// Kroky sa skladajú nezávisle podľa oboch polí (baseRole aj producerRole) - viď
// getRequiredDocumentCategoryKeys v utils/constants/packagingRoles.js. Jednotka tak môže mať
// napr. 6 krokov naraz (Distribútor + Výrobca: doklady, zhoda, EÚ vyhlásenie, technická
// dokumentácia, overenie pred sprístupnením AJ registrácia výrobcu).
const buildWizardSteps = (baseRole, producerRole) =>
    getRequiredDocumentCategoryKeys(baseRole, producerRole).map((key, i) => ({ key, number: i + 1, ...STEP_META[key] }));

const StepCircle = ({ step, isActive, isComplete, onClick }) => {
    const bg = isComplete ? '#198754' : isActive ? '#0B1157' : '#fff';
    const numColor = isActive ? '#4DFFFF' : '#C97D2C';
    const border = isComplete ? 'none' : isActive ? 'none' : '2px solid #f0ad4e';
    const boxShadow = isComplete && isActive ? '0 0 0 3px #0B1157' : 'none';

    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', minWidth: 72 }}
            onClick={onClick}
        >
            <div
                style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: bg, border, boxShadow,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.2s ease, box-shadow 0.2s ease',
                    flexShrink: 0,
                }}
            >
                {isComplete
                    ? <IoCheckmarkCircle size={20} style={{ color: '#fff' }} />
                    : <span style={{ fontSize: 14, fontWeight: 700, color: numColor }}>{step.number}</span>}
            </div>
            <div style={{ marginTop: 6, textAlign: 'center' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: isActive ? '#0B1157' : '#5B6178', lineHeight: 1.2 }}>
                    {step.label}
                </div>
                <div style={{ fontSize: 11, color: '#9AA0B4', lineHeight: 1.2 }}>
                    {step.sublabel}
                </div>
            </div>
        </div>
    );
};

const StepLine = ({ isComplete }) => (
    <div style={{
        flex: 1, height: 2, marginTop: 19,
        background: isComplete ? '#198754' : '#DDDFF0',
        transition: 'background 0.3s ease',
        minWidth: 20,
    }} />
);

const PackagingUnitDetail = ({ unitId }) => {
    const dispatch = useDispatch();
    const unit = useSelector(state => state.packagingUnit.data.find(u => u._id === unitId));
    const [activeTab, setActiveTab] = useState('overview');
    const [photoError, setPhotoError] = useState(false);
    const [zipTooltipPos, setZipTooltipPos] = useState(null);
    const [zipProgress, setZipProgress] = useState(null);
    const [zipPhase, setZipPhase] = useState('queued');

    useEffect(() => { setPhotoError(false); }, [unitId]);

    if (!unit) return null;

    const photoUrl = unit.photo?.filename
        ? `/api/packagingunit/photo?packagingUnitId=${unit._id}&v=${unit.photo.filename}`
        : null;

    const isTabCategoryComplete = (key) => {
        if (key === 'CONFORMITY_ASSESSMENT') return !!unit.conformitySummary?.complete;
        if (key === 'TECHNICAL_DOCS') return !!unit.technicalDocumentationStatus?.current;
        if (key === 'EU_DECLARATION') return !!unit.euDeclarationStatus?.current;
        if (key === 'MARKING') return !!unit.qrCodeGeneratedAt;
        if (key === 'SUPPLIER_DOCS') return !!unit.supplierDocsConfirmedAt;
        if (key === 'DISTRIBUTOR_VERIFICATION') return !!unit.distributorVerificationSummary?.complete;
        if (key === 'PRODUCER_VERIFICATION') return !!unit.producerVerificationSummary?.complete;
        return false;
    };

    const hasTechDoc = !!unit.technicalDocumentationStatus?.current;
    const hasEuDecl = !!unit.euDeclarationStatus?.current;
    const canDownloadZip = hasTechDoc && hasEuDecl;
    const zipTooltip = !hasTechDoc && !hasEuDecl
        ? 'Na stiahnutie je potrebná platná technická dokumentácia aj EÚ vyhlásenie o zhode — ani jedno ešte nebolo vygenerované.'
        : !hasTechDoc
            ? 'Na stiahnutie je potrebná platná technická dokumentácia — ešte nebola vygenerovaná alebo je neaktuálna (jednotka bola upravená po jej vygenerovaní).'
            : 'Na stiahnutie je potrebné platné EÚ vyhlásenie o zhode — ešte nebolo vygenerované alebo je neaktuálne (jednotka bola upravená po jeho vygenerovaní).';

    const WIZARD_STEPS = buildWizardSteps(unit.baseRole, unit.producerRole);
    // Jednotka potrebuje aspoň jeden krok navyše k dokladom od dodávateľa (posúdenie zhody,
    // overenie distribútora, registrácia výrobcu...) - ak nie, zobrazí sa len informačná hláška.
    const hasExtraSteps = WIZARD_STEPS.length > 1;
    const REQUIRED_DOCUMENT_CATEGORY_KEYS = WIZARD_STEPS.map(s => s.key);
    const DOC_STEP_KEYS = WIZARD_STEPS.map(s => s.key);

    const completedCount = REQUIRED_DOCUMENT_CATEGORY_KEYS.filter(isTabCategoryComplete).length;
    const isAllComplete = completedCount === REQUIRED_DOCUMENT_CATEGORY_KEYS.length;
    const isDocActive = DOC_STEP_KEYS.includes(activeTab);

    const mainTabStyle = (active) => ({
        border: 'none',
        borderRadius: 10,
        padding: '7px 14px',
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        backgroundColor: active ? '#0B1157' : '#F1F2F6',
        color: active ? '#4DFFFF' : '#5B6178',
        transition: 'background-color 0.2s ease, color 0.2s ease',
    });

    const docBadge = (() => {
        if (isAllComplete) return { bg: isDocActive ? '#198754' : '#E6F4EA', color: isDocActive ? '#fff' : '#198754' };
        if (completedCount > 0) return { bg: isDocActive ? '#C97D2C' : '#FCEFE3', color: isDocActive ? '#fff' : '#C97D2C' };
        return { bg: isDocActive ? 'rgba(255,255,255,0.12)' : '#E0E2EE', color: isDocActive ? '#8BDDDD' : '#9AA0B4' };
    })();

    return (
        <div>
            {/* Unit header */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 pe-5" style={{ position: 'relative' }}>
                <div
                    style={{
                        width: 64, height: 64, borderRadius: 14, overflow: 'hidden', flexShrink: 0,
                        backgroundColor: '#F0F1F9', border: '1px solid #EDEFF7',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    {photoUrl && !photoError
                        ? <img src={photoUrl} alt={unit.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setPhotoError(true)} />
                        : <IoImageOutline size={24} style={{ color: '#c3c7dc' }} />}
                </div>

                <div className="flex-grow-1">
                    <h4 className="mb-1" style={{ color: '#0B1157' }}>{unit.name}</h4>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {getRoleBadges(unit.baseRole, unit.producerRole).map((badge, i) => (
                            <span key={i} className={`badge bg-${badge.variant}`}>{badge.label}</span>
                        ))}
                        <span className="badge bg-light text-dark border">
                            {PACKAGING_TYPE_LABELS[unit.packagingType] ?? unit.packagingType}
                        </span>
                        <span className="badge bg-light text-dark border">
                            {PACKAGING_CHARACTER_LABELS[unit.character] ?? unit.character}
                        </span>
                        {unit.archivedAt && (
                            <span className="badge bg-secondary">Archivované</span>
                        )}
                    </div>
                </div>
                <div
                    className="flex-shrink-0"
                    onMouseEnter={e => {
                        if (canDownloadZip) return;
                        const r = e.currentTarget.getBoundingClientRect();
                        setZipTooltipPos({ top: r.top - 8, left: r.left + r.width / 2 });
                    }}
                    onMouseLeave={() => setZipTooltipPos(null)}
                >
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        style={{ fontSize: 12 }}
                        disabled={!canDownloadZip}
                        onClick={() => {
                            setZipPhase('queued');
                            setZipProgress(0);
                            dispatch(downloadDocumentationZip({
                                packagingUnitId: unit._id,
                                name: unit.name,
                                onTransferStart: () => setZipPhase('downloading'),
                                onProgress: (p) => setZipProgress(p),
                            }))
                                .finally(() => setZipProgress(null));
                        }}
                    >
                        <IoDownloadOutline size={14} /> Stiahnuť dokumentáciu
                    </Button>
                    {zipTooltipPos && (
                        <div style={{
                            position: 'fixed',
                            top: zipTooltipPos.top,
                            left: zipTooltipPos.left,
                            transform: 'translate(-50%, -100%)',
                            backgroundColor: '#333', color: '#fff', fontSize: 12, padding: '6px 10px',
                            borderRadius: 6, maxWidth: 320, zIndex: 9999, pointerEvents: 'none',
                            lineHeight: 1.5,
                        }}>
                            {zipTooltip}
                        </div>
                    )}
                </div>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-flex align-items-center gap-1 flex-shrink-0"
                    style={{ fontSize: 12 }}
                    onClick={() => dispatch(setPackagingUnitArchived({ packagingUnitId: unit._id, archived: !unit.archivedAt }))}
                >
                    {unit.archivedAt
                        ? <><IoArrowUndoOutline size={14} /> Obnoviť z archívu</>
                        : <><IoArchiveOutline size={14} /> Archivovať</>}
                </Button>
            </div>

            {/* Documentation status */}
            <div className="mb-3">
                <span
                    className="d-inline-flex align-items-center gap-1"
                    style={{
                        fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
                        backgroundColor: isAllComplete ? '#E6F4EA' : '#FCEFE3',
                        color: isAllComplete ? '#198754' : '#C97D2C',
                    }}
                >
                    {isAllComplete
                        ? <IoCheckmarkCircle size={13} />
                        : <IoAlertCircleOutline size={13} />}
                    Dokumentácia {completedCount}/{REQUIRED_DOCUMENT_CATEGORY_KEYS.length} splnená
                </span>
            </div>

            {/* Main navigation */}
            <div
                className="d-flex align-items-center gap-2 mb-3"
                style={{ borderBottom: '1px solid #EEF0F8', paddingBottom: 12 }}
            >
                <button
                    type="button"
                    onClick={() => setActiveTab('overview')}
                    style={mainTabStyle(activeTab === 'overview')}
                >
                    Prehľad
                </button>

                <button
                    type="button"
                    onClick={() => { if (!isDocActive) setActiveTab('SUPPLIER_DOCS'); }}
                    className="d-inline-flex align-items-center gap-2"
                    style={mainTabStyle(isDocActive)}
                >
                    Dokumentácia
                    <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: '1px 6px', borderRadius: 999,
                        backgroundColor: docBadge.bg,
                        color: docBadge.color,
                    }}>
                        {completedCount}/{REQUIRED_DOCUMENT_CATEGORY_KEYS.length}
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => setActiveTab('MARKING')}
                    style={mainTabStyle(activeTab === 'MARKING')}
                >
                    Označenie
                </button>
            </div>

            {/* Vysvetlenie, keď sa neuplatňuje žiadna doplnková povinnosť (dokumentáciu k zhode
                rieši zhotoviteľ obalu, prípadne iný subjekt je výrobcom podľa čl. 3/15) */}
            {isDocActive && !hasExtraSteps && (
                <div className="alert alert-secondary d-flex align-items-start gap-2 mb-4" style={{ fontSize: 13 }}>
                    <IoAlertCircleOutline size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                        Pre túto obalovú jednotku nemusíte riešiť posúdenie zhody, EÚ vyhlásenie ani technickú dokumentáciu — tú zabezpečuje zhotoviteľ obalu (prípadne výrobca podľa čl. 3/15). Stačí, keď si tu uložíte doklady, ktoré od neho dostanete.
                    </div>
                </div>
            )}

            {/* Wizard steps sub-navigation (shown only within Dokumentácia) */}
            {isDocActive && hasExtraSteps && (
                <div
                    className="mb-4"
                    style={{ background: '#F4F5FE', borderRadius: 14, padding: '16px 24px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        {WIZARD_STEPS.map((step, i) => [
                            <StepCircle
                                key={step.key}
                                step={step}
                                isActive={activeTab === step.key}
                                isComplete={isTabCategoryComplete(step.key)}
                                onClick={() => setActiveTab(step.key)}
                            />,
                            i < WIZARD_STEPS.length - 1 && (
                                <StepLine
                                    key={`line-${i}`}
                                    isComplete={isTabCategoryComplete(step.key) && isTabCategoryComplete(WIZARD_STEPS[i + 1].key)}
                                />
                            ),
                        ])}
                    </div>
                </div>
            )}

            {/* Tab content */}
            {activeTab === 'overview' ? (
                <Overview unit={unit} />
            ) : activeTab === 'CONFORMITY_ASSESSMENT' ? (
                <ConformityAssessmentTab unit={unit} description={DOCUMENT_TAB_DESCRIPTIONS.CONFORMITY_ASSESSMENT} />
            ) : activeTab === 'TECHNICAL_DOCS' ? (
                <TechnicalDocumentationTab unit={unit} description={DOCUMENT_TAB_DESCRIPTIONS.TECHNICAL_DOCS} />
            ) : activeTab === 'EU_DECLARATION' ? (
                <EuDeclarationTab unit={unit} description={DOCUMENT_TAB_DESCRIPTIONS.EU_DECLARATION} />
            ) : activeTab === 'MARKING' ? (
                <MarkingTab unit={unit} />
            ) : activeTab === 'DISTRIBUTOR_VERIFICATION' ? (
                <DistributorVerificationTab unit={unit} description={DOCUMENT_TAB_DESCRIPTIONS.DISTRIBUTOR_VERIFICATION} />
            ) : activeTab === 'PRODUCER_VERIFICATION' ? (
                <ProducerVerificationTab unit={unit} description={DOCUMENT_TAB_DESCRIPTIONS.PRODUCER_VERIFICATION} />
            ) : (
                <DocumentsTab unit={unit} category={activeTab} description={DOCUMENT_TAB_DESCRIPTIONS[activeTab]} />
            )}
            {zipProgress !== null && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(11,17,87,0.35)',
                    zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: 14, padding: '28px 32px',
                        minWidth: 320, boxShadow: '0 8px 40px rgba(11,17,87,0.18)',
                    }}>
                        <div style={{ fontWeight: 600, color: '#0B1157', marginBottom: 6, fontSize: 14 }}>
                            {zipPhase === 'queued' ? 'Čakáte v poradí...' : zipProgress < 100 ? `Stiahuje sa... ${zipProgress} %` : 'Dokončujem...'}
                        </div>
                        <div style={{ fontSize: 12, color: '#9AA0B4', marginBottom: 14 }}>
                            {zipPhase === 'queued' ? 'Server spracúva iné požiadavky, spustí sa automaticky.' : 'Prebieha prenos ZIP súboru.'}
                        </div>
                        <div style={{ height: 6, backgroundColor: '#EEF0F8', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                                height: '100%', borderRadius: 3, backgroundColor: '#0B1157',
                                width: zipPhase === 'queued' ? '100%' : `${zipProgress}%`,
                                transition: zipPhase === 'downloading' ? 'width 0.2s ease' : 'none',
                                opacity: zipPhase === 'queued' ? 0.25 : 1,
                            }} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PackagingUnitDetail;
